import apiService from './api';
import type { ApiResponse } from '../types/api';
import type { ChemicalInventoryLocation, PODocument } from '../types/spray';
import type { XeroAccount, XeroContactSummary, XeroTaxRate } from '../types/nurseryAccounts';
import { openDocument } from '../utils/openDocument';

/**
 * Who uses purchase orders, and the few things the pages need that are not
 * the orders themselves. The orders are still read and written through
 * `sprayService` (`/spray/purchase-orders`, from when they were a tab of
 * Spray); purchase orders now belong to no module, and who uses them is the
 * organisation admin's decision.
 */

export interface PurchaseOrderAccess {
  /** Authorised by the organisation's admin: raise, send, receive, cancel. */
  can_raise: boolean;
  /** Authorised, or holding an Accounts seat. */
  can_read: boolean;
  /** An organisation admin: says who is authorised. */
  can_manage: boolean;
}

export interface PurchaseOrderPerson {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string;
  authorised: boolean;
}

/** Purchase orders to the organisation's own Xero, as the Xero page shows it. */
export interface PurchaseOrderXeroSettings {
  /** Xero runs here at all (production only). */
  enabled_here: boolean;
  /** Xero is connected with the permissions purchase orders need. */
  connected: boolean;
  /** Orders are sent to Xero. */
  enabled: boolean;
  account_code: string | null;
  tax_type: string | null;
  queue: { pending: number; failed: number; billed: number };
  failures: { id: number; reference_number: string | null; supplier_name: string | null; xero_sync_error: string | null }[];
  /** After switching on: how many orders already out went to Xero. */
  queued?: number;
}

/** One order's Xero side, as its page shows it. */
export interface PurchaseOrderXero {
  /** Orders go to Xero here: switched on, connected, the live system. */
  active: boolean;
  xero_purchase_order_id: string | null;
  sync_status: 'pending' | 'pushing' | 'synced' | 'failed' | null;
  sync_error: string | null;
  synced_at: string | null;
  /** Xero's own: AUTHORISED, BILLED, DELETED, … */
  xero_status: string | null;
  supplier_name: string | null;
  contact_id: string | null;
  contact: XeroContactSummary | null;
  contact_error: string | null;
}

class PurchaseOrderService {
  async getAccess(): Promise<ApiResponse<PurchaseOrderAccess>> {
    return apiService.get('/spray/purchase-orders/access');
  }

  /** Organisation admin only: the organisation's people, each with whether they are authorised. */
  async getPeople(): Promise<ApiResponse<PurchaseOrderPerson[]>> {
    return apiService.get('/spray/purchase-orders/authorised-users');
  }

  /** Organisation admin only: replace the authorised people. */
  async setAuthorised(userIds: number[]): Promise<ApiResponse<PurchaseOrderPerson[]>> {
    return apiService.put('/spray/purchase-orders/authorised-users', { user_ids: userIds });
  }

  /** Attach a payment receipt (a photo or PDF) to an order. */
  async addReceipt(poId: number, file: File, note?: string): Promise<ApiResponse<PODocument>> {
    const form = new FormData();
    form.append('file', file);
    if (note) form.append('note', note);
    // A phone photo over paddock signal: well past the default 10 s.
    return apiService.post(`/spray/purchase-orders/${poId}/documents`, form, undefined, { timeout: 120000 });
  }

  async removeReceipt(docId: number): Promise<ApiResponse<{ id: number; deleted: boolean }>> {
    return apiService.delete(`/spray/purchase-orders/documents/${docId}`);
  }

  /** The route needs the bearer token, so the file cannot be a plain link. */
  async openReceipt(doc: Pick<PODocument, 'id' | 'file_name'>): Promise<void> {
    await openDocument(`/spray/purchase-orders/documents/${doc.id}/file`, doc.file_name || `receipt-${doc.id}`);
  }

  /** Purchase orders to Xero: any accounts seat reads, an Accounts Admin saves. */
  async getXeroSettings(): Promise<ApiResponse<PurchaseOrderXeroSettings>> {
    return apiService.get('/spray/purchase-orders/xero-settings');
  }

  async saveXeroSettings(settings: { enabled?: boolean; account_code?: string | null; tax_type?: string | null }): Promise<ApiResponse<PurchaseOrderXeroSettings>> {
    return apiService.put('/spray/purchase-orders/xero-settings', settings);
  }

  /** Accounts Admin: Xero's accounts, and the tax rates an expense can carry. */
  async getXeroLists(): Promise<ApiResponse<{ accounts: XeroAccount[]; tax_rates: XeroTaxRate[] }>> {
    return apiService.get('/spray/purchase-orders/xero-settings/lists');
  }

  async getXero(poId: number): Promise<ApiResponse<PurchaseOrderXero>> {
    return apiService.get(`/spray/purchase-orders/${poId}/xero`);
  }

  /** Send it again after a failure. Waits for the attempt: Xero can be slow. */
  async retryXero(poId: number): Promise<ApiResponse<PurchaseOrderXero>> {
    return apiService.post(`/spray/purchase-orders/${poId}/xero/retry`, {}, undefined, { timeout: 60000 });
  }

  /** Accounts Admin: the Xero contact this order's supplier goes to, or null to remove the link. */
  async setSupplierXeroContact(poId: number, contactId: string | null): Promise<ApiResponse<PurchaseOrderXero>> {
    return apiService.put(`/spray/purchase-orders/${poId}/xero/contact`, { contact_id: contactId });
  }

  /** Where received chemical stock can be put. */
  async getLocations(): Promise<ApiResponse<ChemicalInventoryLocation[]>> {
    return apiService.get('/spray/purchase-orders/locations');
  }
}

export default new PurchaseOrderService();
